import { formatLocalDate, localDateInputValue } from '../utils/localDateTime';
import React, { useEffect, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { COUNTRIES, COUNTRY_CODES } from '../common/data';

interface MsaDocument {
    id: string;
    countryCode: string;
    countryName: string;
    title: string;
    version: string;
    effectiveDate: string;
    status: string;
    sha256: string;
    format?: string;
    variables?: string[];
    url: string;
}

const PlatformMsaDocumentManagement: React.FC = () => {
    const [documents, setDocuments] = useState<MsaDocument[]>([]);
    const [supportedVariables, setSupportedVariables] = useState<string[]>([]);
    const [countryName, setCountryName] = useState('');
    const [title, setTitle] = useState('Master Service Agreement');
    const [version, setVersion] = useState('1.0');
    const [effectiveDate, setEffectiveDate] = useState(localDateInputValue());
    const [file, setFile] = useState<File | null>(null);
    const [busy, setBusy] = useState(false);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState('');
    const call = async (action: string, data: Record<string, unknown> = {}) => {
        const callable = httpsCallable(getFunctions(undefined, 'us-central1'), 'manageMsaDocuments');
        return callable({ action, data });
    };
    const load = async () => {
        setLoading(true);
        try {
            const result = (await call('list')).data as { documents?: MsaDocument[]; supportedVariables?: string[] };
            setDocuments(result.documents || []);
            setSupportedVariables(result.supportedVariables || []);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { void load().catch(error => setMessage(error.message)); }, []);
    const upload = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!file || !countryName)
            return;
        setBusy(true);
        setMessage('');
        try {
            const countryCode = (COUNTRY_CODES as any)[countryName]?.iso2 || '';
            if (!/\.docx$/i.test(file.name) || file.type && file.type !== 'application/vnd.openxmlformats-officedocument.wordprocessingml.document')
                throw new Error('Select a DOCX Word document.');
            if (file.size > 10 * 1024 * 1024)
                throw new Error('MSA documents must be under 10 MB.');
            const bytes = new Uint8Array(await file.arrayBuffer());
            let binary = '';
            for (let offset = 0; offset < bytes.length; offset += 32768)
                binary += String.fromCharCode(...bytes.subarray(offset, offset + 32768));
            await call('register', { countryCode, countryName, title, version, effectiveDate, fileBase64: btoa(binary) });
            setFile(null);
            setMessage('MSA document activated.');
            await load();
        } catch (error: any) {
            setMessage(error.message || 'Upload failed.');
        } finally {
            setBusy(false);
        }
    };
    const archive = async (id: string) => {
        setBusy(true);
        try {
            await call('archive', { id });
            await load();
        } catch (error: any) {
            setMessage(error.message || 'Archive failed.');
        } finally {
            setBusy(false);
        }
    };
    const download = async (id: string) => {
        setBusy(true);
        setMessage('');
        try {
            const result = (await call('download', { id })).data as { fileBase64: string; format: string; fileName: string };
            const binary = atob(result.fileBase64);
            const bytes = new Uint8Array(binary.length);
            for (let index = 0; index < binary.length; index++)
                bytes[index] = binary.charCodeAt(index);
            const type = result.format === 'docx' ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' : 'application/pdf';
            const url = URL.createObjectURL(new Blob([bytes], { type }));
            const link = document.createElement('a');
            link.href = url;
            link.download = result.fileName;
            link.click();
            window.setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (error: any) {
            setMessage(error.message || 'Download failed.');
        } finally {
            setBusy(false);
        }
    };
    return <div className="space-y-6">
        <form onSubmit={upload} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-xl font-extrabold text-slate-900">Add Country MSA</h2>
            <p className="mt-1 text-sm text-slate-500">Upload a DOCX template with %SIGNATURE% inside its signature block. It becomes active after validation and PDF preview creation.</p>
            {message && <p className="mt-4 rounded-lg bg-blue-50 px-3 py-2 text-sm text-blue-700">{message}</p>}
            <div className="mt-5 grid gap-4 md:grid-cols-2">
                <label className="text-xs font-bold uppercase text-slate-500">Country<select required value={countryName} onChange={event => setCountryName(event.target.value)} className="mt-1 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm normal-case"><option value="">Select country</option>{COUNTRIES.map(country => <option key={country} value={country}>{country}</option>)}</select></label>
                <label className="text-xs font-bold uppercase text-slate-500">Title<input required value={title} onChange={event => setTitle(event.target.value)} className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm normal-case" /></label>
                <label className="text-xs font-bold uppercase text-slate-500">Version<input required value={version} onChange={event => setVersion(event.target.value)} className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm normal-case" /></label>
                <label className="text-xs font-bold uppercase text-slate-500">Effective Date<input required type="date" value={effectiveDate} onChange={event => setEffectiveDate(event.target.value)} className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm normal-case" /></label>
                <label className="text-xs font-bold uppercase text-slate-500 md:col-span-2">DOCX Document<input required type="file" accept="application/vnd.openxmlformats-officedocument.wordprocessingml.document,.docx" onChange={event => setFile(event.target.files?.[0] || null)} className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm normal-case" /></label>
            </div>
            {supportedVariables.length > 0 && <p className="mt-4 text-xs leading-relaxed text-slate-500">Supported variables: {supportedVariables.map(variable => `%${variable}%`).join(', ')}. Keep each variable within one paragraph.</p>}
            <button disabled={busy} className="mt-5 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-bold text-white disabled:opacity-50">{busy ? 'Saving…' : 'Activate Document'}</button>
        </form>
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <h3 className="px-5 pt-5 text-base font-extrabold text-slate-900">Active Country MSAs</h3>
            <table className="w-full text-left text-sm"><thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr><th className="px-5 py-3">Country</th><th className="px-5 py-3">Document</th><th className="px-5 py-3">Effective</th><th className="px-5 py-3">Actions</th></tr></thead><tbody className="divide-y divide-slate-100">{documents.map(document => <tr key={document.id}><td className="px-5 py-3 font-bold">{document.countryName}</td><td className="px-5 py-3"><p>{document.title} · {document.version}</p><p className="font-mono text-[10px] text-slate-400">{document.sha256?.slice(0, 16)}…</p>{Boolean(document.variables?.length) && <p className="mt-1 text-xs text-slate-500">{document.variables?.map(variable => `%${variable}%`).join(', ')}</p>}</td><td className="px-5 py-3">{formatLocalDate(document.effectiveDate, document.effectiveDate || '')}</td><td className="px-5 py-3"><div className="flex flex-wrap justify-end gap-3"><a href={document.url} target="_blank" rel="noopener noreferrer" className="text-xs font-bold text-blue-600">View PDF</a><button type="button" disabled={busy} onClick={() => void download(document.id)} className="text-xs font-bold text-blue-600 disabled:opacity-50">Download {document.format === 'docx' ? 'DOCX' : 'PDF'}</button><button type="button" disabled={busy} onClick={() => void archive(document.id)} className="text-xs font-bold text-red-600 disabled:opacity-50">Archive</button></div></td></tr>)}</tbody></table>
            {loading
                ? <div role="status" className="flex items-center justify-center gap-3 p-8 text-sm text-slate-500"><div className="h-5 w-5 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />Loading MSA documents…</div>
                : !documents.length && <p className="p-8 text-center text-sm text-slate-400">No active MSA documents.</p>}
        </div>
    </div>;
};

export default PlatformMsaDocumentManagement;
